/**
 * Built-in workflow registry — TypeScript literals checked into the repo.
 *
 * SPEC_MIKE_ADOPTIONS_FOLLOWUP.md #2: each PatPro mode is exposed as a
 * launchable workflow so the composer surface unifies the existing six modes
 * with user-saved prompts. Built-ins do not carry DB rows; the frontend
 * merges this list with the user's saved workflows when rendering.
 *
 * `prompt_md` is markdown that will be prepended to the user message at
 * launch time. Field tokens like `{{patent_number}}` are substituted from the
 * LaunchWorkflowDialog inputs before sending. Fields with `required: true`
 * gate the launch button.
 */
import type { BuiltinWorkflow } from './types';

export const BUILTIN_WORKFLOWS: BuiltinWorkflow[] = [
  {
    id: 'query_patent_law',
    kind: 'assistant',
    name: 'Patent law question',
    description:
      'Ask a research question grounded in the MPEP, statutes, regulations, and CAFC case law.',
    practice: 'patentability',
    prompt_md:
      'Treat this as a patent law research question. Ground every substantive legal statement in the retrieved MPEP, statute, regulation, guidance, or CAFC holding. Cite by section title and source URL — do not fabricate URLs. If the retrieved context is insufficient, say so explicitly.\n\nQuestion: {{question}}',
    fields: [
      {
        id: 'question',
        label: 'Question',
        kind: 'textarea',
        placeholder:
          'e.g., "When does a Markush group claim become improper under § 112?"',
        required: true,
      },
    ],
  },
  {
    id: 'oa_response_draft',
    kind: 'assistant',
    name: 'Office Action response',
    description:
      'Draft a rejection-by-rejection response to an Office Action with citations and authority.',
    practice: 'prosecution',
    prompt_md:
      'Draft a response to the following Office Action. For each rejection: identify the form paragraph, summarize the basis, state the applicable legal standard with authority, and propose response strategies grounded in the retrieved corpus and CAFC holdings. Surface examiner empirical context if available.\n\nApplication: {{application_number}}\n\nOffice Action text:\n\n{{oa_text}}',
    fields: [
      {
        id: 'application_number',
        label: 'Application number',
        kind: 'patent_number',
        placeholder: 'e.g., 12/345,678',
      },
      {
        id: 'oa_text',
        label: 'Office Action text',
        kind: 'textarea',
        placeholder: 'Paste the OA text or upload via the paperclip…',
        required: true,
      },
    ],
  },
  {
    id: 'claim_drafting',
    kind: 'assistant',
    name: 'Claim drafting',
    description:
      'Draft an independent claim from a disclosure with prosecution-aware language and dependent claim ladders.',
    practice: 'prosecution',
    prompt_md:
      'Draft an independent claim and supporting dependent claims from the following disclosure. Use prosecution-aware language: avoid means-plus-function unless intentional, cabin functional language with structure, and respect § 112(b) definiteness norms. Include a brief notes block explaining each claim limitation choice.\n\nDisclosure:\n\n{{disclosure}}',
    fields: [
      {
        id: 'disclosure',
        label: 'Invention disclosure',
        kind: 'textarea',
        placeholder: 'Paste the disclosure or describe the invention…',
        required: true,
      },
    ],
  },
  {
    id: 'claim_chart',
    kind: 'tabular',
    name: 'Claim chart',
    description:
      'Open a tabular claim chart — rows are claim limitations, columns are accused products or prior-art references, each cell editable.',
    practice: 'litigation',
    prompt_md: '',
    fields: [],
  },
  {
    id: 'prosecution_history_analysis',
    kind: 'assistant',
    name: 'Prosecution history analysis',
    description:
      'Analyze the prosecution history of a patent or application — disclaimers, estoppel, examiner positions, claim narrowing.',
    practice: 'prosecution',
    prompt_md:
      "Analyze the prosecution history of the patent / application below. Identify (1) any prosecution disclaimers or disavowals, (2) prosecution-history estoppel triggers from claim amendments, (3) the examiner's key positions across the OA exchanges, and (4) claim language narrowed during prosecution. Cite to the specific transactions or amendments in the file wrapper.\n\nPatent or application: {{patent_number}}",
    fields: [
      {
        id: 'patent_number',
        label: 'Patent or application number',
        kind: 'patent_number',
        placeholder: 'e.g., US 1,234,567 B2 or 12/345,678',
        required: true,
      },
    ],
  },
  {
    id: 'fto_assessment',
    kind: 'assistant',
    name: 'FTO assessment',
    description:
      'Freedom-to-operate analysis against a target product or feature — claim mapping, design-around hooks, risk tiers.',
    practice: 'fto',
    prompt_md:
      'Run a freedom-to-operate analysis for the product / feature described below. For each potentially-relevant patent identified in the retrieved context: (1) state the asserted scope, (2) map the product to each independent claim, (3) flag any limitations the product likely does not meet, (4) note design-around hooks, and (5) categorize risk as high / medium / low with reasoning.\n\nProduct or feature:\n\n{{product_description}}\n\nKnown patents to analyze (optional):\n\n{{known_patents}}',
    fields: [
      {
        id: 'product_description',
        label: 'Product or feature description',
        kind: 'textarea',
        placeholder:
          'Describe the product or feature with enough technical detail to map against claims…',
        required: true,
      },
      {
        id: 'known_patents',
        label: 'Known patents (optional)',
        kind: 'textarea',
        placeholder:
          'e.g., US 1,234,567; US 2,345,678. Leave blank to let PatPro retrieve candidates.',
      },
    ],
  },
];

export function findBuiltin(id: string): BuiltinWorkflow | undefined {
  return BUILTIN_WORKFLOWS.find((w) => w.id === id);
}

/**
 * Substitute `{{field}}` tokens in the prompt body using the user's inputs.
 * Unfilled tokens are left as empty string so the prompt stays readable.
 */
export function applyWorkflowFields(
  template: string,
  values: Record<string, string>,
): string {
  return template.replace(/\{\{(\w+)\}\}/g, (_, key: string) => {
    const v = values[key];
    return typeof v === 'string' ? v.trim() : '';
  });
}
